import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
  CircularProgress,
  Alert,
  FormHelperText,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { examService } from '../../services/apiService';

const ExamForm = ({ open, onClose, onSubmit, initialData, title = "Add Exam" }) => {
  const [formData, setFormData] = useState({
    name: '',
    start_date: '',
    end_date: '',
  });

  const [errors, setErrors] = useState({});

  // Initialize form data when initialData or dialog open changes
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        start_date: initialData.start_date || '',
        end_date: initialData.end_date || '',
      });
    } else {
      // Reset form for new exam
      setFormData({
        name: '',
        start_date: '',
        end_date: '',
      });
    }
    setErrors({});
  }, [initialData, open]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Exam name is required';
    }

    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }

    if (!formData.end_date) {
      newErrors.end_date = 'End date is required';
    }

    // Validate that end date is after start date
    if (formData.start_date && formData.end_date && formData.start_date > formData.end_date) {
      newErrors.end_date = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const submitData = { ...formData };
      
      // Include ID for updates
      if (initialData?.id) {
        submitData.id = initialData.id;
      }

      // No data conversion needed for the simplified fields

      await onSubmit(submitData);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  }, [formData, initialData?.id, validateForm, onSubmit]);

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {title}
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box component="form" sx={{ mt: 1 }}>
            {/* Exam Name */}
            <TextField
              fullWidth
              margin="normal"
              label="Exam Name"
              value={formData.name}
              onChange={handleChange('name')}
              error={!!errors.name}
              helperText={errors.name}
              required
            />

            {/* Start Date */}
            <TextField
              fullWidth
              margin="normal"
              label="Start Date"
              type="date"
              value={formData.start_date}
              onChange={handleChange('start_date')}
              error={!!errors.start_date}
              helperText={errors.start_date}
              InputLabelProps={{
                shrink: true,
              }}
              required
            />

            {/* End Date */}
            <TextField
              fullWidth
              margin="normal"
              label="End Date"
              type="date"
              value={formData.end_date}
              onChange={handleChange('end_date')}
              error={!!errors.end_date}
              helperText={errors.end_date}
              InputLabelProps={{
                shrink: true,
              }}
              required
            />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
        >
          {initialData?.id ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExamForm;
